import { keyframes } from '@emotion/react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardVoiceRoundedIcon from '@mui/icons-material/KeyboardVoiceRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import { Box, Button, ButtonBase, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import React from 'react';
import { CharacterGrade, improveGrades, scoreMessage, speakingScore } from '../../mockLessonContent';
import { BLUE, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, yellowButtonSx } from '../../studentStyles';
import ProgressRing from '../ProgressRing';
import PronunciationDetail from './PronunciationDetail';

const micPulse = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.18); }
    100% { transform: scale(1); }
`;

const MIN_HOLD_MS = 400;
const PROCESSING_MS = 1800;

type Phase = 'idle' | 'recording' | 'processing' | 'scored';

interface RecordAnswerPanelProps {
    grades: CharacterGrade[];
    // Ungraded prompts (the …NoGrade classes) only acknowledge the recording.
    graded?: boolean;
    idleLabel?: string;
    // Tighter layout for the record control inside a conversation line.
    compact?: boolean;
    onScored: () => void;
}

// Hold-to-record → processing → result, shared by every prompt that ends in the
// student speaking. Fake audio: the recording is never captured or sent.
export default function RecordAnswerPanel({
    grades: initialGrades,
    graded = true,
    idleLabel = 'Hold to answer',
    compact = false,
    onScored,
}: RecordAnswerPanelProps) {
    const [phase, setPhase] = React.useState<Phase>('idle');
    const [attempt, setAttempt] = React.useState(0);
    const [grades, setGrades] = React.useState(initialGrades);
    const [detailOpen, setDetailOpen] = React.useState(false);
    const [showHoldHint, setShowHoldHint] = React.useState(false);
    const pressStartRef = React.useRef(0);
    const timeoutRef = React.useRef<number>();

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const startRecording = () => {
        if (phase !== 'idle') {
            return;
        }
        setShowHoldHint(false);
        pressStartRef.current = Date.now();
        setPhase('recording');
    };

    const finishRecording = () => {
        if (phase !== 'recording') {
            return;
        }
        if (Date.now() - pressStartRef.current < MIN_HOLD_MS) {
            setPhase('idle');
            setShowHoldHint(true);
            return;
        }
        setPhase('processing');
        timeoutRef.current = window.setTimeout(() => {
            const nextAttempt = attempt + 1;
            setAttempt(nextAttempt);
            if (nextAttempt >= 2) {
                setGrades(prev => improveGrades(prev));
            }
            setPhase('scored');
            onScored();
        }, PROCESSING_MS);
    };

    const isRecording = phase === 'recording';
    const isProcessing = phase === 'processing';
    const score = speakingScore(grades);
    const message = scoreMessage(score);

    const recordAgainButton = (
        <Button
            startIcon={<MicRoundedIcon />}
            onClick={() => { setDetailOpen(false); setPhase('idle'); }}
            sx={{
                mt: 2, fontFamily: INTER, fontWeight: 700, fontSize: 14, color: BLUE,
                border: `1px solid ${RING_TRACK}`, borderRadius: '999px', px: 2.5, py: 0.75,
            }}
        >
            Record again
        </Button>
    );

    if (phase === 'scored') {
        if (!graded) {
            return (
                <Stack alignItems="center" sx={{ mt: compact ? 1.5 : 3 }}>
                    <CheckCircleRoundedIcon sx={{ color: GREEN, fontSize: 40 }} />
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 16, color: NAVY, mt: 1 }}>
                        Got it — 说得好!
                    </Typography>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.5, textAlign: 'center' }}>
                        This one is not scored — speaking up is the whole point.
                    </Typography>
                    {recordAgainButton}
                </Stack>
            );
        }
        return (
            <Stack alignItems="center" sx={{ mt: compact ? 1.5 : 3 }}>
                <ProgressRing percent={score} size={compact ? 64 : 84} thickness={4} showLabel />
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: compact ? 16 : 18, color: NAVY, mt: 1.5 }}>
                    {message.title}
                </Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.5, textAlign: 'center' }}>
                    {message.subtitle}
                </Typography>
                <ButtonBase
                    onClick={() => setDetailOpen(prev => !prev)}
                    sx={{ mt: 1.5, borderRadius: '999px', px: 1.5, py: 0.5, fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE, gap: 0.5 }}
                >
                    See details
                    <ExpandMoreRoundedIcon sx={{ fontSize: 20, transform: detailOpen ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }} />
                </ButtonBase>
                <Collapse in={detailOpen} sx={{ width: '100%' }}>
                    <Box sx={{ mt: 1.5 }}>
                        <PronunciationDetail grades={grades} />
                    </Box>
                </Collapse>
                {recordAgainButton}
            </Stack>
        );
    }

    return (
        <>
            <Button
                fullWidth
                variant="contained"
                onPointerDown={startRecording}
                onPointerUp={finishRecording}
                onPointerLeave={finishRecording}
                onPointerCancel={finishRecording}
                onContextMenu={event => event.preventDefault()}
                startIcon={isProcessing
                    ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                    : <KeyboardVoiceRoundedIcon sx={isRecording ? { animation: `${micPulse} 900ms ease-in-out infinite` } : undefined} />}
                sx={{
                    ...yellowButtonSx,
                    ...(isRecording || isProcessing
                        ? { backgroundColor: BLUE, color: '#fff', '&:hover': { backgroundColor: BLUE } }
                        : {}),
                    ...(compact ? { fontSize: '14px', py: 1 } : { py: 1.5 }),
                    mt: compact ? 1.5 : 3,
                    pointerEvents: isProcessing ? 'none' : 'auto',
                    touchAction: 'none',
                    userSelect: 'none',
                }}
            >
                {isProcessing
                    ? graded ? 'Checking your pronunciation…' : 'Saving your recording…'
                    : isRecording
                        ? 'Listening… release when done'
                        : idleLabel}
            </Button>
            {showHoldHint && (
                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, textAlign: 'center', mt: 1 }}>
                    Hold the button while you speak
                </Typography>
            )}
        </>
    );
}
